import { useEffect, useRef } from 'react';
import { Scenario } from '../../types';

export interface UseTestManagerShortcutsOptions {
  currentScenario: Scenario | null;
  categories: { name: string }[];
  currentCategory: string;
  isAddingNotes: boolean;
  onStatusChange: (status: string) => void;
  onNavigateNext: () => void;
  onNavigatePrevious: () => void;
  onClearScenario: () => void;
  onDuplicateScenario: (scenario: Scenario) => void;
  onEditScenario: (scenario: Scenario) => void;
  setShortcutsVisible: (value: boolean | ((prev: boolean) => boolean)) => void;
  setAddingNotes: (value: boolean | ((prev: boolean) => boolean)) => void;
  notesTextareaRef: React.RefObject<HTMLTextAreaElement | null>;
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

export function useTestManagerShortcuts({
  currentScenario,
  isAddingNotes,
  onStatusChange,
  onNavigateNext,
  onNavigatePrevious,
  onClearScenario,
  onDuplicateScenario,
  onEditScenario,
  setShortcutsVisible,
  setAddingNotes,
  notesTextareaRef,
  showToast,
}: UseTestManagerShortcutsOptions): void {
  const handlersRef = useRef({
    onStatusChange,
    onNavigateNext,
    onNavigatePrevious,
    onClearScenario,
    onDuplicateScenario,
    onEditScenario,
    setShortcutsVisible,
    setAddingNotes,
    showToast,
  });
  handlersRef.current = {
    onStatusChange,
    onNavigateNext,
    onNavigatePrevious,
    onClearScenario,
    onDuplicateScenario,
    onEditScenario,
    setShortcutsVisible,
    setAddingNotes,
    showToast,
  };

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLInputElement) {
        if (event.key === 'Escape' && isAddingNotes) {
          handlersRef.current.setAddingNotes(false);
          notesTextareaRef.current?.blur();
        }
        return;
      }

      const key = event.key.toLowerCase();

      switch (key) {
        case 'p':
          if (currentScenario) {
            handlersRef.current.onStatusChange('Passed');
            handlersRef.current.showToast('Status zmieniony na Passed', 'success');
          }
          break;
        case 'f':
          if (currentScenario) {
            handlersRef.current.onStatusChange('Failed');
            handlersRef.current.showToast('Status zmieniony na Failed', 'success');
          }
          break;
        case 'b':
          if (currentScenario) {
            handlersRef.current.onStatusChange('Blocked');
            handlersRef.current.showToast('Status zmieniony na Blocked', 'success');
          }
          break;
        case 'n':
          if (currentScenario) {
            handlersRef.current.onStatusChange('Not Tested');
            handlersRef.current.showToast('Status zmieniony na Not Tested', 'success');
          }
          break;
        case 'arrowright':
          handlersRef.current.onNavigateNext();
          break;
        case 'arrowleft':
          handlersRef.current.onNavigatePrevious();
          break;
        case 'h':
          handlersRef.current.setShortcutsVisible((prev) => !prev);
          break;
        case 't':
          if (currentScenario) {
            handlersRef.current.setAddingNotes((prev) => {
              const next = !prev;
              if (!next && notesTextareaRef.current) {
                setTimeout(() => notesTextareaRef.current?.focus(), 0);
              }
              return next;
            });
          }
          break;
        case 'escape':
          if (isAddingNotes) {
            handlersRef.current.setAddingNotes(false);
            notesTextareaRef.current?.blur();
          } else if (currentScenario) {
            handlersRef.current.onClearScenario();
          }
          break;
        case 'd':
          if (currentScenario && event.ctrlKey) {
            handlersRef.current.onDuplicateScenario(currentScenario);
            handlersRef.current.showToast('Scenariusz został zduplikowany', 'success');
          }
          break;
        case 'e':
          if (currentScenario && event.ctrlKey) {
            handlersRef.current.onEditScenario(currentScenario);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentScenario, isAddingNotes, notesTextareaRef]);
}
